/**
 * Partner Earnings Calculator — user-facing tab inside Commissions.
 *
 * Any authenticated user (partner / admin / staff) can compute
 * indicative earnings. Backend enforces which fields are returned —
 * partners never receive vendor cost, net margin, or MIRO retained
 * income. Admins get an extra breakdown card.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Card } from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { Loader2, Calculator, ShieldCheck } from "lucide-react";
import BusinessPlanBlock from "@/pages/BusinessPlanBlock";

const inr = (n) =>
  n == null ? "—" : "₹" + Number(n).toLocaleString("en-IN");

const inputHintByProduct = {
  professional_course:   { fields: ["customer_fee"], defaults: { customer_fee: 34000 } },
  language_testprep:     { fields: ["customer_fee"], defaults: { customer_fee: 14999 } },
  inhouse_bde:           { fields: [] },
  inhouse_fs:            { fields: [] },
  inhouse_uae:           { fields: [] },
  inhouse_sac:           { fields: [] },
  inhouse_scp:           { fields: [] },
  inhouse_bilingual_de:  { fields: [] },
  inhouse_bilingual_fr:  { fields: [] },
  insurance_health:      { fields: ["premium"] },
  insurance_life:        { fields: ["premium"] },
  insurance_renewal:     { fields: ["premium"] },
  loan:                  { fields: ["disbursed_amount"] },
  mutual_fund:           { fields: ["aum", "sip_monthly", "years", "growth_rate"] },
  pms_aif:               { fields: ["ticket_size"] },
  bonds_ipo:             { fields: ["disbursed_amount"] },
  indian_admission:      { fields: ["tuition_amount", "tuition_basis"] },
  study_abroad:          { fields: ["tuition_amount", "tuition_basis", "country", "institution"] },
  mbbs_abroad:           { fields: ["country", "institution"] },
};

// Optional per-product info banner shown in the partner result card so
// partners understand headline sharing terms without exposing internals.
const PARTNER_PRODUCT_NOTES = {
  bonds_ipo:
    "Partner earns 90% of MIRO's net commission on eligible bonds, "
    + "unlisted shares and IPO opportunities. TDS is deducted from the "
    + "partner's share before payout.",
  language_testprep:
    "Enter the customer fee. The system will match it to the nearest "
    + "listed course and show your indicative earning. TDS is deducted "
    + "from your share before payout.",
};

const FIELD_LABEL = {
  customer_fee:      "Customer fee (₹)",
  premium:           "Premium (₹)",
  disbursed_amount:  "Disbursed / commission-base amount (₹)",
  aum:              "Existing lump-sum AUM (₹)",
  sip_monthly:      "Monthly SIP (₹)",
  years:            "Number of years",
  growth_rate:      "Illustrative growth rate (0.10 = 10%)",
  ticket_size:      "Ticket size (₹)",
  tuition_amount:   "Eligible tuition (₹)",
  tuition_basis:    "Tuition basis",
  country:          "Country",
  institution:      "Institution",
};

export default function EarningsCalculatorTab() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  // "isAdmin" gates the admin sandbox overrides (staff can preview tiers)
  const isAdmin = ["super_admin", "founder", "finance"].includes(user?.role);

  const [products, setProducts] = useState([]);
  const [productId, setProductId] = useState("");
  const [inputs, setInputs] = useState({});
  const [adminOverride, setAdminOverride] = useState(
    { partner_type: "professional", annual_admissions: 0 });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/earnings/products").then(r => {
      // Language / Test-Prep courses are managed as a per-course
      // catalog with fee + net payout visible to everyone. There's no
      // need to also show a generic "Language / Test-Preparation"
      // product in the calculator dropdown — hide it.
      const filtered = (r.data || []).filter(
        p => p.product_id !== "language_testprep");
      setProducts(filtered);
      if (filtered.length && !productId) setProductId(filtered[0].product_id);
    }).catch(() => setError("Could not load products"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const hint = inputHintByProduct[productId];
    if (hint?.defaults) setInputs(hint.defaults);
    else setInputs({});
    setResult(null);
  }, [productId]);

  const currentProduct = useMemo(
    () => products.find(p => p.product_id === productId),
    [products, productId],
  );
  const fields = inputHintByProduct[productId]?.fields || [];

  const calculate = async () => {
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const payload = { product_id: productId, ...inputs };
      if (payload.years) payload.years = Number(payload.years);
      if (isAdmin) {
        payload.admin_override_partner_type = adminOverride.partner_type;
        payload.admin_override_annual_admissions = Number(
          adminOverride.annual_admissions);
      }
      const r = await api.post("/earnings/calculate", payload);
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Calculation failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <Card className="lg:col-span-2 p-5 space-y-4"
            data-testid="calculator-inputs-card">
        <div className="flex items-center gap-2">
          <Calculator size={18}/>
          <h3 className="text-lg font-semibold">Estimate your earning</h3>
        </div>

        <div>
          <Label>Product / Service</Label>
          <Select value={productId} onValueChange={setProductId}>
            <SelectTrigger data-testid="calc-product-select"><SelectValue/></SelectTrigger>
            <SelectContent>
              {products.map(p => (
                <SelectItem key={p.product_id} value={p.product_id}>
                  {p.product_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {fields.map(f => (
          <div key={f}>
            <Label>{FIELD_LABEL[f] || f}</Label>
            {f === "tuition_basis" ? (
              <Select value={inputs[f] || "first_year"}
                      onValueChange={v => setInputs(s => ({ ...s, [f]: v }))}>
                <SelectTrigger data-testid={`calc-field-${f}`}><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="first_year">First year eligible tuition</SelectItem>
                  <SelectItem value="total">Total eligible tuition</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Input
                data-testid={`calc-field-${f}`}
                type={["country", "institution"].includes(f) ? "text" : "number"}
                value={inputs[f] ?? ""}
                onChange={e => setInputs(s =>
                  ({ ...s, [f]: e.target.value === "" ? undefined : e.target.value }))}
              />
            )}
          </div>
        ))}

        {isAdmin && (
          <Card className="p-3 bg-amber-50 border-amber-200 space-y-2">
            <div className="text-xs font-medium text-amber-900 flex items-center gap-1">
              <ShieldCheck size={12}/> Admin sandbox (preview any tier)
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Partner type</Label>
                <Select value={adminOverride.partner_type}
                        onValueChange={v => setAdminOverride(s => ({ ...s, partner_type: v }))}>
                  <SelectTrigger data-testid="calc-override-partner-type" className="h-8"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="franchise">Franchise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Annual admissions</Label>
                <Input data-testid="calc-override-admissions"
                       type="number" className="h-8"
                       value={adminOverride.annual_admissions}
                       onChange={e => setAdminOverride(s =>
                          ({ ...s, annual_admissions: e.target.value }))}/>
              </div>
            </div>
          </Card>
        )}

        <Button data-testid="calc-submit-btn"
                onClick={calculate} disabled={loading || !productId}
                className="w-full">
          {loading ? <Loader2 className="animate-spin" size={16}/> : "Calculate"}
        </Button>
        {error && (
          <div data-testid="calc-error"
               className="text-sm text-red-600">{error}</div>
        )}
      </Card>

      <div className="lg:col-span-3 space-y-4">
        {result ? (
          <>
            <ResultCard view={result.partner_view} isAdmin={false}
                        productId={productId}/>
            {isSuperAdmin && result.admin_view && (
              <ResultCard view={result.admin_view} isAdmin={true}
                          productId={productId}/>
            )}
            {isSuperAdmin && result.context && (
              <Card className="p-3 text-xs text-slate-600">
                <span className="font-medium">Context:</span>{" "}
                partner_type={result.context.partner_type}, annual_admissions={result.context.annual_admissions}
              </Card>
            )}
          </>
        ) : (
          <Card className="p-8 text-center text-slate-500"
                data-testid="calc-empty-state">
            Choose a product and enter details to see indicative earnings.
          </Card>
        )}
      </div>
    </div>
    <BusinessPlanBlock/>
    <LanguageCourseEarnings/>
    </div>
  );
}


function ResultCard({ view, isAdmin, productId }) {
  const badgeColor = view.tier === "High-Volume Tier"
    ? "bg-emerald-100 text-emerald-800"
    : view.tier === "Franchise Tier"
    ? "bg-indigo-100 text-indigo-800"
    : "bg-slate-100 text-slate-700";
  const note = !isAdmin && PARTNER_PRODUCT_NOTES[productId];

  return (
    <Card className={`p-5 space-y-3 ${isAdmin ? "border-amber-300 bg-amber-50/40" : ""}`}
          data-testid={isAdmin ? "calc-admin-result" : "calc-partner-result"}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wide text-slate-500">
            {isAdmin ? "Admin breakdown" : "Your indicative earning"}
          </div>
          <div className="text-lg font-semibold">{view.product}</div>
        </div>
        {view.tier && (
          <Badge className={badgeColor} data-testid="calc-tier-badge">
            {view.tier}
          </Badge>
        )}
      </div>

      {note && (
        <div className="p-2 rounded bg-blue-50 border border-blue-200 text-xs text-blue-900"
             data-testid="calc-product-note">
          {note}
        </div>
      )}

      {view.annual_admissions != null && view.tier && (
        <div className="text-sm text-slate-600">
          Annual completed admissions: <b>{view.annual_admissions}</b>
          {view.threshold ? ` of ${view.threshold}` : ""}
          {view.admissions_to_next_tier ? (
            <span className="text-emerald-700 ml-2">
              Complete {view.admissions_to_next_tier} more to unlock the next payout tier.
            </span>
          ) : null}
        </div>
      )}

      {view.unlisted && (
        <div className="p-3 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900"
             data-testid="calc-unlisted-message">
          {view.message}
        </div>
      )}

      {view.matched_fee_tier != null && !view.unlisted && (
        <div className="text-xs text-slate-500"
             data-testid={isAdmin ? "calc-matched-tier-admin" : "calc-matched-tier"}>
          Matched to listed fee tier: <b>{inr(view.matched_fee_tier)}</b>
          {view.matched_courses_count > 1
            ? ` (${view.matched_courses_count} courses at this fee — range shown)`
            : ""}
        </div>
      )}

      {view.customer_fee != null && (
        <Row label="Customer fee" value={inr(view.customer_fee)}/>
      )}
      {view.base_amount != null && (
        <Row label="Amount" value={inr(view.base_amount)}/>
      )}
      {view.tuition_amount != null && (
        <Row label="Eligible tuition" value={inr(view.tuition_amount)}/>
      )}
      {view.ticket_size != null && (
        <Row label="Ticket size" value={inr(view.ticket_size)}/>
      )}
      {view.aum != null && (
        <>
          <Row label="Starting AUM" value={inr(view.aum)}/>
          <Row label="Monthly SIP" value={inr(view.sip_monthly)}/>
          <Row label="Trail rate" value={view.trail_rate_pct + "% / yr"}/>
        </>
      )}

      {view.partner_gross_earning != null && (
        <>
          <Row label="Indicative gross earning"
               value={inr(view.partner_gross_earning)} bold/>
          <Row label="Estimated TDS" value={inr(view.estimated_tds)}/>
          <Row label="Indicative net payout"
               value={inr(view.indicative_net_payout)} highlight/>
        </>
      )}

      {view.partner_gross_earning_min != null && (
        <>
          <Row label="Indicative gross earning"
               value={`${inr(view.partner_gross_earning_min)} – ${inr(view.partner_gross_earning_max)}`}
               bold/>
          <Row label="Estimated TDS"
               value={`${inr(view.estimated_tds_min)} – ${inr(view.estimated_tds_max)}`}/>
          <Row label="Indicative net payout"
               value={`${inr(view.indicative_net_payout_min)} – ${inr(view.indicative_net_payout_max)}`}
               highlight/>
        </>
      )}

      {view.year1_estimated_earning != null && (
        <>
          <Row label="Year 1 estimated earning" value={inr(view.year1_estimated_earning)} bold/>
          <Row label="3-year cumulative" value={inr(view.cumulative_3yr)}/>
          <Row label="5-year cumulative" value={inr(view.cumulative_5yr)} highlight/>
          {view.cumulative_10yr && (
            <Row label="10-year cumulative" value={inr(view.cumulative_10yr)}/>
          )}
        </>
      )}

      {view.milestones && (
        <div className="mt-2 text-sm">
          <div className="font-medium mb-1">Milestone progress</div>
          <ol className="list-decimal list-inside text-slate-600 text-xs space-y-0.5">
            {view.milestones.map((m, i) => <li key={i}>{m}</li>)}
          </ol>
        </div>
      )}

      {isAdmin && (
        <div className="mt-2 pt-2 border-t border-amber-200 space-y-1 text-xs text-slate-700">
          {view.taxable_customer_value != null && (
            <Row label="Taxable customer value" value={inr(view.taxable_customer_value)}/>
          )}
          {view.output_gst != null && (
            <Row label="Output GST" value={inr(view.output_gst)}/>
          )}
          {view.direct_cost != null && (
            <Row label="Direct cost" value={inr(view.direct_cost)}/>
          )}
          {view.net_margin != null && (
            <Row label="Net margin" value={inr(view.net_margin)}/>
          )}
          {view.miro_retained != null && (
            <Row label="MIRO retained" value={inr(view.miro_retained)}/>
          )}
          {view.applied_share_pct != null && (
            <Row label="Applied partner share" value={view.applied_share_pct + "%"}/>
          )}
          {view.applied_partner_share_pct != null && (
            <Row label="Applied partner share" value={view.applied_partner_share_pct + "%"}/>
          )}
          {view.provider_commission_range_pct && (
            <Row label="Provider commission range"
                 value={view.provider_commission_range_pct.join("% – ") + "%"}/>
          )}
          {view.rate_card_version && (
            <Row label="Rate card version" value={"v" + view.rate_card_version}/>
          )}
          {view.course_breakdown && (
            <div className="mt-2 pt-2 border-t border-amber-200">
              <div className="font-medium mb-1">Courses at this fee tier</div>
              <div className="max-h-56 overflow-y-auto text-xs">
                <table className="w-full">
                  <thead>
                    <tr className="text-left text-slate-500">
                      <th>Course</th><th className="text-right">Direct cost</th>
                      <th className="text-right">Net payout</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.course_breakdown.map((c, i) => (
                      <tr key={i} className="border-t border-amber-100">
                        <td className="py-0.5">{c.course}</td>
                        <td className="py-0.5 text-right">{inr(c.direct_cost)}</td>
                        <td className="py-0.5 text-right font-medium text-emerald-700">
                          {inr(c.indicative_net_payout)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!isAdmin && view.settlement && (
        <div className="mt-3 pt-3 border-t text-xs text-slate-500">
          <div className="font-medium mb-1">Settlement</div>
          {view.settlement}
        </div>
      )}
      {!isAdmin && view.disclaimer && (
        <div className="text-[10px] text-slate-400 leading-relaxed">
          {view.disclaimer}
        </div>
      )}
    </Card>
  );
}

function Row({ label, value, bold, highlight }) {
  return (
    <div className={`flex justify-between text-sm ${highlight ? "text-emerald-700 font-semibold" : ""}`}>
      <span className="text-slate-600">{label}</span>
      <span className={bold ? "font-semibold" : ""}>{value}</span>
    </div>
  );
}


/* ---------------------------------------------------------------------------
 * Language / Test-Preparation earnings — per-course lookup for every role.
 * Direct cost is stripped server-side; only Fee + Net payout are shown.
 * ------------------------------------------------------------------------- */
function LanguageCourseEarnings() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get("/earnings/language-courses")
      .then(r => setRows(r.data?.courses || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const q = query.trim().toLowerCase();
  const visible = q
    ? rows.filter(c =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.category || "").toLowerCase().includes(q))
    : rows;

  const grouped = visible.reduce((acc, c) => {
    (acc[c.category] ||= []).push(c); return acc;
  }, {});

  return (
    <div className="mt-8 space-y-3" data-testid="lang-courses-partner-view">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">
            Language &amp; Test-Preparation — Earnings per course
          </h3>
          <p className="text-xs text-slate-500">
            Look up your indicative payout for each listed course. Figures
            use the standard 70% partner share and 2% TDS.
          </p>
        </div>
        <Input placeholder="Search course..." className="w-64"
               value={query} onChange={e => setQuery(e.target.value)}
               data-testid="lang-courses-search"/>
      </div>

      {loading ? (
        <Card className="p-6 text-center text-sm text-slate-500">
          <Loader2 className="inline h-4 w-4 animate-spin mr-1"/>Loading catalog…
        </Card>
      ) : Object.keys(grouped).length === 0 ? (
        <Card className="p-6 text-center text-sm text-slate-500">
          No matching courses.
        </Card>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <div key={cat}>
            <div className="text-sm font-medium text-slate-700 mb-1">{cat}</div>
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2">Course</th>
                    <th className="text-right px-3 py-2">Customer fee</th>
                    <th className="text-right px-3 py-2">Estimated TDS</th>
                    <th className="text-right px-3 py-2">Indicative net payout</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((c, i) => (
                    <tr key={i} className="border-t"
                        data-testid={`lang-course-earning-${cat}-${i}`}>
                      <td className="px-3 py-2">{c.name}</td>
                      <td className="px-3 py-2 text-right">₹{Number(c.fee).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right">₹{Number(c.estimated_tds).toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right font-medium text-emerald-700">
                        ₹{Number(c.indicative_net_payout).toLocaleString("en-IN")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        ))
      )}
    </div>
  );
}
